/**
 * RunDecisionsSection — the run-record page's Decisions list (26742).
 *
 * Presentation only: the caller (the route) builds `lines` via
 * `buildDecisionLines` from whichever of `decisions` / HITL history it
 * managed to fetch — this component never knows which source produced a
 * line, only how to render one.
 *
 * Line format: `${summary} · ${actorName} · via ${surfaceLabel}` — the
 * trailing "via ..." clause is OMITTED (not rendered as "via ") whenever the
 * line carries no recognised surface, exactly as the contract specifies.
 */
import { useMemo, useState } from "react";
import ArtifactLightbox from "../components/ArtifactLightbox";
import {
  isLightboxViewable,
  formatCapturedAt,
} from "../components/WorkflowArtifactsSection";
import type { WorkflowArtifact } from "../components/WorkflowArtifactsSection";
import {
  uriToArtifact,
  HitlEventDetail,
} from "../components/WorkflowHitlHistorySection";
import { surfaceLabel } from "./types";
import type { DecisionLine } from "./decisionLines";

export interface RunDecisionsSectionProps {
  lines: DecisionLine[];
  /** All-details view. Adds the surface a decision came in through ("via
   *  Outlook add-in"). The Servicer view keeps the line to what happened and
   *  who did it — which surface they used is provenance, not the decision. */
  showProvenance?: boolean;
}

function DecisionRow({
  line,
  showProvenance,
}: {
  line: DecisionLine;
  showProvenance: boolean;
}) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const detailBodyId = `decision-detail-${line.key}`;

  const artifacts = useMemo<WorkflowArtifact[]>(
    () =>
      (line.artifactsSnapshot ?? []).map(uriToArtifact).filter(isLightboxViewable),
    [line.artifactsSnapshot],
  );

  const surface = showProvenance ? surfaceLabel(line.surface) : null;
  const text = [line.summary, line.actorName, surface ? `via ${surface}` : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <li
      className={`flex flex-col gap-1 rounded-md border border-primary-gray-light-200 bg-base-white px-3 py-2 ${
        line.dimmed ? "opacity-50" : ""
      }`}
      data-testid="decision-line"
      data-source={line.source}
      data-failed={line.failed ? "true" : "false"}
      data-dimmed={line.dimmed ? "true" : "false"}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-primary-gray-light-900">{text}</span>
        {line.ts && (
          <span className="text-xs text-primary-gray-light-500 flex-shrink-0">
            {formatCapturedAt(line.ts)}
          </span>
        )}
      </div>

      {line.sourceMessageLost && (
        <div
          className="rounded border border-primary-warning-300 bg-primary-warning-50 px-2 py-1 text-xs text-primary-warning-800"
          data-testid="decision-line-source-lost-banner"
        >
          the Servicer moved or deleted the request
        </div>
      )}

      <div className="flex items-center gap-3">
        {artifacts.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="self-start text-xs text-sunrise hover:underline"
              data-testid="decision-line-what-was-shown"
            >
              what was shown ›
            </button>
            <ArtifactLightbox
              open={lightboxOpen}
              artifacts={artifacts}
              startIndex={0}
              onClose={() => setLightboxOpen(false)}
            />
          </>
        )}

        {/* Drill-down into the HITL event behind this line. The ledger stays a
            ledger; the detail is the SAME renderer the history cards use, so
            the prompt-as-shown, the schema-aware response and the artifact
            strip cannot drift between the two places they appear. */}
        {line.hitlEvent && (
          <button
            type="button"
            onClick={() => setDetailOpen((open) => !open)}
            aria-expanded={detailOpen}
            aria-controls={detailBodyId}
            className="self-start text-xs text-sunrise hover:underline"
            data-testid="decision-line-detail-toggle"
          >
            {detailOpen ? "hide detail" : "what was asked ›"}
          </button>
        )}
      </div>

      {line.hitlEvent && detailOpen && (
        <HitlEventDetail event={line.hitlEvent} bodyId={detailBodyId} />
      )}
    </li>
  );
}

export function RunDecisionsSection({
  lines,
  showProvenance = true,
}: RunDecisionsSectionProps) {
  if (lines.length === 0) {
    return (
      <p
        className="text-sm text-primary-gray-light-600"
        data-testid="run-decisions-empty"
      >
        No decisions are visible to you for this run.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-2" data-testid="run-decisions-list">
      {lines.map((line) => (
        <DecisionRow key={line.key} line={line} showProvenance={showProvenance} />
      ))}
    </ul>
  );
}

export default RunDecisionsSection;
